// /log-cash — Modul 3: manualni zapis hotovostnich plateb mimo FAPI.
// Data v PG tabulce log_cash. Pocet treninku dopocita backend uz pri zapisu
// (log-cash.js, LC_PACKAGE_MAP). Cron processLogCash byl zrusen 9. 10. 2026.
//
// Features:
//   - 2 KPI dlazdice (Celkem zaznamu, Celkem CZK)
//   - Search + filter mesic + rok
//   - Modal Pridat platbu s fuzzy autocomplete klientu + dropdown balicky (predvyplnene)
//   - Confirm dialog pred Pridat / Smazat
//   - Optimistic update + audit log
//
// 9.9.2026 (fáze 4b): sjednoceno do vzorníku. Akce v hlavičce jsou ikony, KPI
// jsou `Dlazdice`, okna jedou přes `Modal` (na telefonu zespodu, patka nad
// home indikátorem), pole přes `.field`/`.label`, emoji v hláškách nahradily
// ikony. Na telefonu je z tabulky seznam, na počítači tabulka zůstává.
// Endpointy, optimistický zápis ani audit se nemění.

import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Banknote, Plus, RefreshCw, AlertTriangle, ArrowLeft, Search, Trash2,
  CheckCircle2, Package,
} from "lucide-react";
import Card from "../components/Card.jsx";
import GradientHeader from "../components/GradientHeader.jsx";
import InfoTip from "../components/InfoTip.jsx";
import ClientAutocomplete, { CLIENT_AUTOCOMPLETE_TOOLTIP, normalizeLoose } from "../components/ClientAutocomplete.jsx";
import { Stranka, Modal, Field, Dlazdice, Stitek, EmptyState, SkeletonRows, Hlaska, AkceZnovu } from "../components/ui.jsx";
import { useUzkaObrazovka } from "../lib/obrazovka.js";
import { useClientsList } from "../lib/clientsCache.js";
import { useApiData, apiFetch } from "../lib/api.js";
import { getCurrentUser } from "../lib/auth.js";
import { formatDate as fmtDate } from "../lib/format.js";
import { toast } from "../components/Toast.jsx";

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return fmtDate(d);
}
function fmtCzk(n) {
  return Number(n || 0).toLocaleString("cs-CZ", { maximumFractionDigits: 0 }) + " Kč";
}

/** Potvrzovací okno. Zelené pro přidání, červené pro smazání. */
function PotvrzeniOkno({ title, message, confirmLabel, cancelLabel, variant, onConfirm, onCancel }) {
  const nebezpecne = variant === "danger";
  const Ikona = nebezpecne ? AlertTriangle : CheckCircle2;
  return (
    <Modal
      open onClose={onCancel} title={title} size="sm"
      footer={
        <>
          <button type="button" onClick={onCancel} className="btn btn-secondary">{cancelLabel || "Zrušit"}</button>
          <button type="button" onClick={onConfirm} className={nebezpecne ? "btn btn-danger" : "btn btn-primary"}>{confirmLabel || "Potvrdit"}</button>
        </>
      }
    >
      <div className="flex items-start gap-3">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${nebezpecne ? "bg-rose-100 text-rose-600" : "bg-emerald-100 text-emerald-600"}`}>
          <Ikona className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1 text-sm text-slate-600">{message}</div>
      </div>
    </Modal>
  );
}

function AddModal({ onClose, onSubmit, submitting, error, klientOptions, balicky, packageMap }) {
  const [klient, setKlient] = useState("");
  const [datum, setDatum] = useState(new Date().toISOString().substring(0, 10));
  const [typBalicku, setTypBalicku] = useState(balicky[0] || "");
  const [castka, setCastka] = useState("");
  const [confirmShown, setConfirmShown] = useState(false);

  const castkaNum = parseFloat(String(castka).replace(/\s/g, "").replace(",", "."));
  const canSubmit = klient.trim().length > 0 && datum && typBalicku && Number.isFinite(castkaNum) && castkaNum > 0;
  const treninkuPredikce = typBalicku && packageMap ? packageMap[typBalicku] : null;

  const buildPayload = () => ({ action: "add", klient: klient.trim(), datum, typBalicku, castka: castkaNum });

  return (
    <>
      <Modal
        open onClose={onClose} title="Přidat hotovostní platbu" size="sm"
        footer={
          <>
            <button type="button" onClick={onClose} className="btn btn-secondary">Zrušit</button>
            <button type="button" onClick={() => setConfirmShown(true)} disabled={submitting || !canSubmit} className="btn btn-primary">
              {submitting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {submitting ? "Ukládám…" : "Přidat platbu"}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <div className="label flex items-center gap-1.5">Klient <InfoTip text={CLIENT_AUTOCOMPLETE_TOOLTIP} /></div>
            <ClientAutocomplete value={klient} onChange={setKlient} options={klientOptions} accentColor="violet" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Datum platby">
              <input type="date" value={datum} onChange={(e) => setDatum(e.target.value)} required className="field" />
            </Field>
            <Field label="Částka v Kč">
              <input type="text" inputMode="numeric" value={castka} onChange={(e) => setCastka(e.target.value)}
                placeholder="např. 4990" className="field" />
            </Field>
          </div>

          <Field label="Typ balíčku" hint={treninkuPredikce != null ? `Dopočítá se ${treninkuPredikce} tréninků dle mapování balíčků.` : undefined}>
            <select value={typBalicku} onChange={(e) => setTypBalicku(e.target.value)} className="field">
              {balicky.map((b) => (
                <option key={b} value={b}>{b} ({packageMap?.[b] || "?"} tréninků)</option>
              ))}
            </select>
          </Field>

          {error && <Hlaska ton="problem" title="Uložení se nepovedlo">{error}</Hlaska>}

          <Hlaska ton="pozor" title="Audit log">
            Záznam se uloží i s tvým jménem.
          </Hlaska>
        </div>
      </Modal>

      {confirmShown && (
        <PotvrzeniOkno
          title="Přidat hotovostní platbu?"
          message={
            <div className="space-y-1">
              <div><strong className="text-slate-900">{klient}</strong></div>
              <div className="text-xs text-slate-500">
                {formatDate(datum)}, {typBalicku}, <span className="font-semibold text-emerald-700">{fmtCzk(castkaNum)}</span>
              </div>
              {treninkuPredikce !== null && (
                <div className="text-xs text-slate-500">Dopočítá se {treninkuPredikce} tréninků.</div>
              )}
            </div>
          }
          confirmLabel="Ano, přidat"
          cancelLabel="Zpět"
          onConfirm={() => { setConfirmShown(false); onSubmit(buildPayload()); }}
          onCancel={() => setConfirmShown(false)}
        />
      )}
    </>
  );
}

export default function LogCash() {
  const navigate = useNavigate();
  const uzka = useUzkaObrazovka();
  const { data, setData, loading, error, refetch, refreshing } = useApiData("/api/log-cash");
  const currentUser = getCurrentUser();
  const canDelete = currentUser && ["owner", "manager"].includes(currentUser.role);

  const _now = new Date();
  const [filterMonth, setFilterMonth] = useState(String(_now.getMonth() + 1));
  const [filterYear, setFilterYear] = useState(String(_now.getFullYear()));
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const klientOptions = useClientsList();
  const [deleteTarget, setDeleteTarget] = useState(null);

  const stats = data?.stats || {};
  const entries = data?.entries || [];
  const balicky = data?.balicky || [];
  const packageMap = data?.packageMap || {};

  // Parser datumu — zvladne YYYY-MM-DD a D.M.YYYY (Sheet ulkadu jako D.M.YYYY).
  function parseDateMY(raw) {
    const s = String(raw || "").trim();
    if (!s) return null;
    const iso = s.match(/^(\d{4})-(\d{1,2})-/);
    if (iso) return { year: iso[1], month: String(parseInt(iso[2], 10)) };
    const cz = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})/);
    if (cz) return { year: cz[3], month: String(parseInt(cz[2], 10)) };
    return null;
  }

  const roky = useMemo(() => {
    const set = new Set();
    entries.forEach((e) => {
      const p = parseDateMY(e.datum);
      if (p) set.add(p.year);
    });
    return [...set].sort().reverse();
  }, [entries]);

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      if (filterMonth !== "all" || filterYear !== "all") {
        const p = parseDateMY(e.datum);
        if (!p) return false;
        if (filterMonth !== "all" && p.month !== filterMonth) return false;
        if (filterYear !== "all" && p.year !== filterYear) return false;
      }
      if (search) {
        const sNorm = normalizeLoose(search);
        const tokens = sNorm.split(" ").filter(Boolean);
        const hay = normalizeLoose(`${e.klient} ${e.typBalicku}`);
        if (!tokens.every((t) => hay.includes(t))) return false;
      }
      return true;
    }).sort((a, b) => {
      const aIso = a.datum.match(/^\d{4}-/) ? a.datum.substring(0, 10)
        : (() => { const m = a.datum.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})/); return m ? `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}` : ""; })();
      const bIso = b.datum.match(/^\d{4}-/) ? b.datum.substring(0, 10)
        : (() => { const m = b.datum.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})/); return m ? `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}` : ""; })();
      return bIso.localeCompare(aIso);
    });
  }, [entries, filterMonth, filterYear, search]);

  const filteredCastka = useMemo(() => filtered.reduce((s, e) => s + (e.castka || 0), 0), [filtered]);

  // Optimistic add
  const handleAdd = async (payload) => {
    setSubmitError(null);
    setModalOpen(false);
    const tmpId = `tmp_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
    const optimistic = {
      _tmp: tmpId,
      rowNum: 99999,
      klient: payload.klient,
      datum: payload.datum,
      typBalicku: payload.typBalicku,
      pocetTreninku: packageMap[payload.typBalicku] || null,
      castka: payload.castka,
    };
    setData((prev) => prev ? {
      ...prev,
      entries: [...(prev.entries || []), optimistic],
      stats: {
        ...prev.stats,
        totalRows: (prev.stats?.totalRows || 0) + 1,
        totalCastka: (prev.stats?.totalCastka || 0) + payload.castka,
      },
    } : prev);
    try {
      const res = await apiFetch("/api/log-cash", { method: "POST", body: JSON.stringify(payload) });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      refetch();
    } catch (e) {
      setSubmitError(e.message);
      toast(`Chyba uložení: ${e.message}`, "error");
      refetch();
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null);
    setData((prev) => prev ? {
      ...prev,
      entries: (prev.entries || []).filter((e) => e.rowNum !== target.rowNum),
    } : prev);
    try {
      const res = await apiFetch("/api/log-cash", {
        method: "POST",
        body: JSON.stringify({ action: "delete", rowNum: target.rowNum, klient: target.klient }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      refetch();
    } catch (e) {
      toast(`Chyba mazání: ${e.message}`, "error");
      refetch();
    }
  };

  const MESICE_NAMES = ["leden", "únor", "březen", "duben", "květen", "červen", "červenec", "srpen", "září", "říjen", "listopad", "prosinec"];

  return (
    <Stranka>
      <GradientHeader
        title="Log Cash"
        icon={Banknote}
        loading={loading}
        akce={[
          { klic: "zpet", ikona: ArrowLeft, popis: "Zpět na klienty", onClick: () => navigate("/clients") },
          { klic: "pridat", ikona: Plus, popis: "Přidat platbu", onClick: () => { setSubmitError(null); setModalOpen(true); } },
          { klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch, disabled: loading || refreshing },
        ]}
      />

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Dlazdice popisek="Celkem záznamů (rok)" hodnota={stats.totalRows || 0} ton="ok"
          doplnky={<InfoTip text="Počet hotovostních plateb napříč všemi měsíci letošního roku." />} />
        <Dlazdice popisek="Celkem CZK (rok)" hodnota={fmtCzk(stats.totalCastka || 0)} ton="znacka"
          doplnky={<InfoTip text="Sumární hotovostní obrat letos, součet všech hotovostních plateb v databázi." />} />
      </div>

      <Card title="Záznamy" ikona={Banknote} lista odznak={data ? `${filtered.length} z ${entries.length}` : undefined}>
        <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-[minmax(200px,1fr)_auto_auto_auto]">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Hledat klienta nebo balíček…" aria-label="Hledat" className="field pl-9" />
          </div>
          <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} aria-label="Filtrovat dle měsíce"
            className={`field ${filterMonth !== "all" ? "border-brand font-semibold text-brand-deep" : ""}`}>
            <option value="all">Všechny měsíce</option>
            {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
              <option key={m} value={String(m)}>{MESICE_NAMES[m - 1]}</option>
            ))}
          </select>
          <select value={filterYear} onChange={(e) => setFilterYear(e.target.value)} aria-label="Filtrovat dle roku"
            className={`field ${filterYear !== "all" ? "border-brand font-semibold text-brand-deep" : ""}`}>
            <option value="all">Všechny roky</option>
            {roky.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button type="button" onClick={() => { setSubmitError(null); setModalOpen(true); }} className="btn btn-primary btn-sm">
            <Plus className="h-4 w-4" /> Přidat platbu
          </button>
        </div>

        {filtered.length > 0 && (
          <div className="mb-3 flex items-center gap-2">
            <Stitek ton="ok">Součet výběru {fmtCzk(filteredCastka)}</Stitek>
          </div>
        )}

        {error && <Hlaska ton="problem" title="Záznamy se nenačetly" akce={<AkceZnovu onClick={refetch} />}>{String(error)}</Hlaska>}

        {loading && entries.length === 0 ? (
          <SkeletonRows rows={6} />
        ) : !error && filtered.length === 0 ? (
          <EmptyState icon={Banknote} title="Žádné záznamy" hint="Nic neodpovídá filtru." />
        ) : uzka ? (
          <div className="space-y-2">
            {filtered.map((e, i) => (
              <div key={`${e.rowNum}-${i}-${e._tmp || ""}`} className={`polozka ${e._tmp ? "opacity-60" : ""}`}>
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[15px] font-bold leading-snug text-slate-900 demo-sensitive">{e.klient}</div>
                    <div className="mt-0.5 text-[12.5px] tabular-nums text-slate-500">{formatDate(e.datum)}</div>
                  </div>
                  <span className="shrink-0 text-[15px] font-black tabular-nums text-emerald-700">{fmtCzk(e.castka)}</span>
                  {canDelete && (
                    <button type="button" onClick={() => setDeleteTarget(e)} title="Smazat záznam" aria-label="Smazat záznam"
                      className="btn-ikona shrink-0 border-rose-200 text-rose-500 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <Stitek ton="znacka" velikost="mala" ikona={Package}>{e.typBalicku}</Stitek>
                  {e.pocetTreninku !== null && <Stitek ton="neutralni" velikost="mala">{e.pocetTreninku}× trénink</Stitek>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="-mx-4 overflow-x-auto sm:-mx-5">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-slate-200 bg-brand-50/60 text-xs uppercase tracking-wider text-slate-500">
                  <th className="whitespace-nowrap px-4 py-3 text-left font-semibold sm:px-5">Datum</th>
                  <th className="px-2 py-3 text-left font-semibold">Klient</th>
                  <th className="px-2 py-3 text-left font-semibold">Typ balíčku</th>
                  <th className="whitespace-nowrap px-2 py-3 text-right font-semibold">Tréninků</th>
                  <th className="whitespace-nowrap px-2 py-3 text-right font-semibold">Částka</th>
                  {canDelete && <th className="w-14"></th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((e, i) => (
                  <tr key={`${e.rowNum}-${i}-${e._tmp || ""}`} className={`border-b border-slate-100 hover:bg-brand-50/50 ${e._tmp ? "opacity-60" : ""}`}>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-700 sm:px-5">{formatDate(e.datum)}</td>
                    <td className="px-2 py-3 font-medium text-slate-900 demo-sensitive">{e.klient}</td>
                    <td className="px-2 py-3 text-slate-700">
                      <Stitek ton="znacka" velikost="mala" ikona={Package}>{e.typBalicku}</Stitek>
                    </td>
                    <td className="px-2 py-3 text-right font-semibold tabular-nums text-slate-900">
                      {e.pocetTreninku !== null ? `${e.pocetTreninku}×` : <span className="text-xs text-slate-400">—</span>}
                    </td>
                    <td className="whitespace-nowrap px-2 py-3 text-right font-bold tabular-nums text-emerald-700">{fmtCzk(e.castka)}</td>
                    {canDelete && (
                      <td className="px-2 py-3">
                        <button type="button" onClick={() => setDeleteTarget(e)} title="Smazat záznam" aria-label="Smazat záznam"
                          className="btn-ikona border-rose-200 text-rose-500 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {modalOpen && (
        <AddModal
          onClose={() => setModalOpen(false)} onSubmit={handleAdd}
          submitting={false} error={submitError}
          klientOptions={klientOptions} balicky={balicky} packageMap={packageMap}
        />
      )}

      {deleteTarget && (
        <PotvrzeniOkno
          title="Smazat záznam Log Cash?"
          message={
            <div className="space-y-1">
              <div><strong className="text-slate-900">{deleteTarget.klient}</strong></div>
              <div className="text-xs text-slate-500">
                {formatDate(deleteTarget.datum)}, {deleteTarget.typBalicku}, {fmtCzk(deleteTarget.castka)}
              </div>
              <div className="mt-2 flex items-start gap-1.5 text-xs text-rose-700">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>Tato akce je nevratná. Záznam se smaže z databáze. Tréninky z fakturace u klienta se přepočítají při další denní aktualizaci faktur.</span>
              </div>
            </div>
          }
          confirmLabel="Ano, smazat"
          cancelLabel="Zrušit"
          variant="danger"
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </Stranka>
  );
}
